import { type DriveFile } from '@/shared/DriveTypes';
import { VisibilityOutlined } from '@mui/icons-material';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import cx from 'classnames';
import { bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useEffect, useLayoutEffect, useReducer } from 'react';
import { createPortal } from 'react-dom';
import * as buttonStyles from '~/elements/Buttons/Button.css';
import { tooltipEnterDelay } from '~/utils/constants';
import { getId, ls } from '~/utils/utils';
import { tooltipSlotProps, tooltipSlots } from '../Tooltip';
import * as styles from './SheetSettings.css';

const setIframe = (src: string) => {
  const tables = document.querySelectorAll('[data-table]');
  const iframe = document.querySelector('[data-iframe]');
  if (!tables[0]) return;
  if (!iframe) return;
  if (!(iframe instanceof HTMLIFrameElement)) return;
  const linksContainer = document.querySelector('[data-links]');
  tables.forEach((table) => table.classList.add('hide'));
  if (linksContainer) linksContainer.classList.add('hide');
  iframe.classList.remove('hide');
  iframe.src = src;
};

export const SheetMacro = ({ file, query }: { file: DriveFile; query: string }) => {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const settingsPopupState = usePopupState({ variant: 'popover' });
  const setView = (view: string) => {
    if (view === '') ls.remove('sheet-view');
    else ls.set('sheet-view', view);
  };

  // TODO: add keyboard shortcut
  // useKeyPress('', () => {
  //   settingsPopupState.setAnchorEl(document.querySelector(query));
  //   settingsPopupState.open();
  // });

  useLayoutEffect(() => {
    const view = ls.get<string>('sheet-view');
    if (!view) return;
    const id = getId(file);
    setIframe(`https://docs.google.com/spreadsheets/d/${id}/${view}`);
    const linksContainer = document.querySelector('[data-links]');
    if (linksContainer) linksContainer.classList.add('hide');
  }, [file]);

  useEffect(() => {
    if (!file) return;
    const nodes = Array.from(document.querySelectorAll(query));
    const emptyChildren = nodes.filter((node) => !node.hasChildNodes());
    if (nodes.length && emptyChildren.length) {
      forceUpdate();
    }
  }, [file, query]);

  const nodes = Array.from(document.querySelectorAll(query));
  if (!nodes.length) return null;

  return nodes.map((node) =>
    createPortal(
      <>
        <Tooltip {...tooltipEnterDelay} slotProps={tooltipSlotProps} slots={tooltipSlots} title="View settings">
          <div
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              // marginRight: 4,
            }}
          >
            <VisibilityOutlined
              style={{ fontSize: 30, margin: '0px 2px' }}
              className={cx([styles.button, buttonStyles.buttonCircular])}
              htmlColor="inherit"
              {...bindTrigger(settingsPopupState)}
            />
          </div>
        </Tooltip>
        <Menu
          slotProps={{
            paper: {
              style: {
                marginTop: 0,
              },
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transitionDuration={{
            enter: 200,
            exit: 0,
          }}
          open={settingsPopupState.isOpen}
          onClose={() => settingsPopupState.close()}
          anchorEl={settingsPopupState.anchorEl}
        >
          <MenuItem
            onClick={() => {
              settingsPopupState.close();
              const tables = document.querySelectorAll('[data-table]');
              const iframe = document.querySelector('[data-iframe]');
              if (!tables[0]) return;
              if (!iframe) return;
              if (!(iframe instanceof HTMLIFrameElement)) return;
              tables[0].classList.remove('hide');
              const linksContainer = document.querySelector('[data-links]');
              if (linksContainer) linksContainer.classList.remove('hide');
              iframe.classList.add('hide');
              iframe.src = '';
              setView('');
            }}
          >
            Default
          </MenuItem>
          <MenuItem
            onClick={() => {
              settingsPopupState.close();
              const id = getId(file);
              setIframe(`https://docs.google.com/spreadsheets/d/${id}/preview`);
              setView('preview');
            }}
          >
            Read only
          </MenuItem>
          <MenuItem
            onClick={() => {
              settingsPopupState.close();
              const id = getId(file);
              setIframe(`https://docs.google.com/spreadsheets/d/${id}/edit?rm=minimal`);
              setView('edit?rm=minimal');
            }}
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              settingsPopupState.close();
              const id = getId(file);
              setIframe(`https://docs.google.com/spreadsheets/d/${id}/edit?rm=demo`);
              setView('edit?rm=demo');
            }}
          >
            Edit with toolbar
          </MenuItem>
          <MenuItem
            onClick={() => {
              settingsPopupState.close();
              const id = getId(file);
              setIframe(`https://docs.google.com/spreadsheets/d/${id}/edit?rm=embedded`);
              setView('edit?rm=embedded');
            }}
          >
            Edit with menu
          </MenuItem>
        </Menu>
      </>,
      node
    )
  );
};
